import { X, Eye, Copy } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { StatusModal } from "./StatusModal";
import type { AuthFieldInstance, IntegrationAccess, IntegrationAuthType } from "@/schema";

interface StatusMessage {
  type: "success" | "error";
  message: string;
}

export type CustomIntegrationSubmitPayload = {
  type: IntegrationAuthType;
  instanceLabel: string;
  values: Record<string, string>;
  instanceId?: string;
  requiresIpWhitelist: boolean;
  access: IntegrationAccess;
  sandbox: boolean;
};

interface CustomIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;

  title: string;
  subtitle?: string;

  type: IntegrationAuthType;
  fields: AuthFieldInstance[];

  // For edit mode: pass existing label (instance.displayInfo.title)
  initialInstanceLabel?: string;

  instanceId?: string;
  showAllowedIps?: boolean;
  // Static platform egress IPs surfaced by the server config (mcp-outlet getServers).
  // When non-empty, the whitelist UI is rendered regardless of showAllowedIps.
  allowedEgressIps?: string[];
  initialRequiresIpWhitelist?: boolean;
  sandboxEnabled?: boolean;
  initialSandbox?: boolean;
  initialAccess: IntegrationAccess;
  onSubmit: (payload: CustomIntegrationSubmitPayload) => Promise<void>;
  onSuccess?: () => void | Promise<void>;
}

const normalizeAccess = (access: IntegrationAccess): IntegrationAccess => ({
  tools: { ...access.tools },
  entities: {
    mode: access.entities.mode,
    allow: [...access.entities.allow],
  },
});

const parseEntityList = (value: string) =>
  value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const visualFieldKey = (label: string, fallback: string) => {
  const slug = String(label || fallback || "field")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || String(fallback || "field").replace(/[^a-zA-Z0-9_-]+/g, "-");
};

export const CustomIntegrationModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  type,
  fields,
  initialInstanceLabel,
  onSubmit,
  onSuccess,
  instanceId,
  showAllowedIps = false,
  allowedEgressIps = [],
  initialRequiresIpWhitelist = false,
  sandboxEnabled = false,
  initialSandbox = false,
  initialAccess,
}: CustomIntegrationModalProps) => {
  const [instanceLabel, setInstanceLabel] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [requiresIpWhitelist, setRequiresIpWhitelist] = useState(
    initialRequiresIpWhitelist === true
  );
  const [sandbox, setSandbox] = useState(false);
  const [access, setAccess] = useState<IntegrationAccess>(() =>
    normalizeAccess(initialAccess)
  );
  const [entityAllowText, setEntityAllowText] = useState("");
  const lastInitKeyRef = useRef<string | null>(null);

  // Reveal-on-button for sensitive fields (1s)
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const revealTimeoutsRef = useRef<Record<string, number>>({});

  const clearRevealTimeout = (key: string) => {
    const t = revealTimeoutsRef.current[key];
    if (t) {
      window.clearTimeout(t);
      delete revealTimeoutsRef.current[key];
    }
  };

  const revealFieldFor1s = (key: string) => {
    clearRevealTimeout(key);
    setRevealed((p) => ({ ...p, [key]: true }));
    revealTimeoutsRef.current[key] = window.setTimeout(() => {
      setRevealed((p) => ({ ...p, [key]: false }));
      delete revealTimeoutsRef.current[key];
    }, 1000);
  };

  useEffect(() => {
    return () => {
      Object.keys(revealTimeoutsRef.current).forEach((k) =>
        clearRevealTimeout(k)
      );
    };
  }, []);

  // Snapshot of initial values to compute "changed fields" on submit
  const initial = useMemo(() => {
    const snap: Record<string, string> = {};
    for (const f of fields) {
      if (!f.isSecret) {
        const v = typeof f.value === "string" ? f.value : "";
        snap[f.key] = v;
      } else {
        snap[f.key] = "";
      }
    }
    return snap;
  }, [fields]);

  const fieldInitKey = useMemo(
    () =>
      fields
        .map((field) => {
          const value = !field.isSecret && typeof field.value === "string" ? field.value : "";
          return `${field.key}:${field.isSecret ? "secret" : value}`;
        })
        .join("|"),
    [fields]
  );
  const accessInitKey = JSON.stringify(initialAccess);

  const modalInitKey = [
    instanceId || "new",
    title,
    type,
    initialInstanceLabel || "",
    sandboxEnabled ? "sandbox-enabled" : "sandbox-disabled",
    initialSandbox ? "sandbox" : "live",
    fieldInitKey,
    accessInitKey,
  ].join("::");

  useEffect(() => {
    if (!isOpen) {
      lastInitKeyRef.current = null;
      setInstanceLabel("");
      setValues({});
      setTouched({});
      setRevealed({});
      setSubmitting(false);
      setStatus(null);
      setRequiresIpWhitelist(false);
      setSandbox(false);
      setAccess(normalizeAccess(initialAccess));
      setEntityAllowText("");

      Object.keys(revealTimeoutsRef.current).forEach((k) =>
        clearRevealTimeout(k)
      );
      return;
    }

    if (lastInitKeyRef.current === modalInitKey) {
      return;
    }
    lastInitKeyRef.current = modalInitKey;

    // Init label for edit mode
    setInstanceLabel((initialInstanceLabel || "").trim());

    // Init field values
    const next: Record<string, string> = {};
    for (const f of fields) {
      if (f.isSecret) {
        next[f.key] = "";
      } else {
        next[f.key] = typeof f.value === "string" ? f.value : "";
      }
    }
    setValues(next);

    setTouched({});
    setRevealed({});
    setSubmitting(false);
    setStatus(null);
    setSandbox(sandboxEnabled && initialSandbox);
    const nextAccess = normalizeAccess(initialAccess);
    setAccess(nextAccess);
    setEntityAllowText(nextAccess.entities.allow.join(", "));

    Object.keys(revealTimeoutsRef.current).forEach((k) =>
      clearRevealTimeout(k)
    );
  }, [isOpen, modalInitKey]);

  const setFieldValue = (key: string, next: string) => {
    setValues((prev) => ({ ...prev, [key]: next }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};

    // instanceLabel required if no existing label (create mode)
    const labelTrim = instanceLabel.trim();
    const hasExistingLabel = (initialInstanceLabel || "").trim().length > 0;
    const requiresConnectionLabel = type === "custom" || fields.length > 0;
    if (requiresConnectionLabel && !hasExistingLabel && labelTrim.length === 0) {
      errors.__instanceLabel = "Required";
    }

    for (const f of fields) {
      const v = values[f.key] ?? "";
      const isEmpty = v.trim() === "";

      const isSensitiveInput =
        f.isSecret || f.inputType === "password" || f.inputType === "token";

      if (f.required) {
        if (!isSensitiveInput && isEmpty) {
          errors[f.key] = "Required";
          continue;
        }

        if (isSensitiveInput) {
          const existingMasked = typeof f.value === "string" ? f.value : "";
          const hasExisting = existingMasked.trim() !== "";
          if (!hasExisting && isEmpty) {
            errors[f.key] = "Required";
            continue;
          }
        }
      }

      if (f.inputType === "select") {
        const opts = (f as any).options ?? [];
        if (!isEmpty && Array.isArray(opts) && opts.length > 0) {
          const ok = opts.some((o: any) => o.value === v);
          if (!ok) errors[f.key] = "Invalid selection";
        }
      }
    }

    return errors;
  };

  const errors = useMemo(
    () => validate(),
    [fields, values, instanceLabel, initialInstanceLabel, type]
  );

  const hasConnectionFields = fields.length > 0;
  const shouldShowConnectionFields = type === "custom" || hasConnectionFields;
  const canUseSetupModal = type === "custom" || type === "oauth";

  const canSubmit =
    isOpen &&
    canUseSetupModal &&
    Object.keys(errors).length === 0 &&
    !submitting;

  const buildAccessPayload = (): IntegrationAccess => {
    const entitiesMode = access.entities.mode;
    return {
      tools: {
        read: true,
        write: Boolean(access.tools.write),
      },
      entities: {
        mode: entitiesMode,
        allow: entitiesMode === "allow" ? parseEntityList(entityAllowText) : [],
      },
    };
  };

  const buildChangedPayload = (): Record<string, string> => {
    const out: Record<string, string> = {};

    for (const f of fields) {
      const current = (values[f.key] ?? "").trim();
      const baseline = (initial[f.key] ?? "").trim();

      if (f.isSecret) {
        if (current !== "") out[f.key] = current;
        continue;
      }

      if (current !== baseline) out[f.key] = current;
    }

    return out;
  };

  const handleSubmit = async () => {
    const currentErrors = validate();
    if (Object.keys(currentErrors).length > 0) {
      const allTouched: Record<string, boolean> = { __instanceLabel: true };
      for (const f of fields) allTouched[f.key] = true;
      setTouched(allTouched);
      setStatus({
        type: "error",
        message: "Please fix the highlighted fields.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const changed = buildChangedPayload();

      await onSubmit({
        type,
        instanceId: instanceId,
        instanceLabel:
          instanceLabel.trim() || (initialInstanceLabel || "").trim(),
        values: changed,
        requiresIpWhitelist: requiresIpWhitelist,
        access: buildAccessPayload(),
        sandbox,
      });

      setStatus({ type: "success", message: "Saved successfully." });
      if (onSuccess) await onSuccess();
    } catch (e: any) {
      setStatus({
        type: "error",
        message: e?.message || "Failed to save settings",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const labelError = errors.__instanceLabel;
  const showLabelError = !!labelError && !!touched.__instanceLabel;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--modal-backdrop)] backdrop-blur-sm">
        <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--modal-bg)] rounded-xl border border-[var(--border-primary)] shadow-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 sm:p-8">
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">{title}</h2>
              {subtitle ? (
                <p className="text-sm text-[var(--text-secondary)] mt-1">{subtitle}</p>
              ) : null}
            </div>

            <div className="mt-6">
              {type !== "custom" && type !== "oauth" ? (
                <div className="text-sm text-[var(--text-secondary)]">
                  This modal supports setup fields only.
                </div>
              ) : (
                <>
                  {shouldShowConnectionFields ? (
                    <>
                  {/* Instance label */}
                  <div className="mb-6">
                    <label
                      htmlFor="custom-auth-connection-name"
                      className="block text-sm text-[var(--text-secondary)]"
                    >
                      Connection name
                      {!(initialInstanceLabel || "").trim() ? (
                        <span className="text-[var(--text-muted)]"> *</span>
                      ) : null}
                    </label>
                    <div className="text-xs text-[var(--text-muted)] mt-1">
                      This name will be used to identify the connection for you
                      and the assistant.
                    </div>

                    <input
                      id="custom-auth-connection-name"
                      name="connection-name"
                      data-auth-field="connection-name"
                      data-auth-field-key="connection-name"
                      data-auth-field-label="Connection name"
                      type="text"
                      className={[
                        "mt-2 w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]",
                        showLabelError
                          ? "border-red-500/60 focus:border-red-500"
                          : "border-[var(--border-primary)] focus:border-[var(--border-accent)]",
                      ].join(" ")}
                      value={instanceLabel}
                      placeholder="e.g. Prod, Staging, Acme, Client A"
                      onChange={(e) => {
                        setInstanceLabel(e.target.value);
                        setTouched((p) => ({ ...p, __instanceLabel: true }));
                      }}
                      onBlur={() =>
                        setTouched((p) => ({ ...p, __instanceLabel: true }))
                      }
                      autoComplete="off"
                      spellCheck={false}
                    />

                    {showLabelError ? (
                      <div className="text-xs text-red-400 mt-1">
                        {labelError}
                      </div>
                    ) : null}
                  </div>

                  {sandboxEnabled ? (
                    <label
                      htmlFor="custom-auth-connect-to-sandbox"
                      className="mb-6 flex items-center gap-3 rounded-lg border border-[var(--border-primary)] bg-[var(--surface)] px-3 py-2 cursor-pointer"
                    >
                      <input
                        id="custom-auth-connect-to-sandbox"
                        name="connect-to-sandbox"
                        data-auth-field="connect-to-sandbox"
                        data-auth-field-key="sandbox"
                        data-auth-field-label="Connect to sandbox"
                        type="checkbox"
                        checked={sandbox}
                        onChange={(event) => setSandbox(event.target.checked)}
                        className="h-4 w-4 rounded border-[var(--border-primary)] text-[var(--accent-blue)]"
                      />
                      <span className="text-sm text-[var(--text-secondary)]">
                        Connect to sandbox
                      </span>
                    </label>
                  ) : null}

                  {/* IP Whitelist Checkbox - shown when the server supplies egress IPs
                      (preferred) or when the legacy showAllowedIps prop is true. */}
                  {(allowedEgressIps.length > 0 || showAllowedIps) && (
                    <div className="mb-6">
                      <label
                        htmlFor="custom-auth-requires-ip-whitelist"
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <input
                          id="custom-auth-requires-ip-whitelist"
                          name="requires-ip-whitelist"
                          data-auth-field="requires-ip-whitelist"
                          data-auth-field-key="requiresIpWhitelist"
                          data-auth-field-label="My service requires IP whitelisting"
                          type="checkbox"
                          checked={requiresIpWhitelist}
                          onChange={(e) =>
                            setRequiresIpWhitelist(e.target.checked)
                          }
                          className="mt-0.5 w-4 h-4 rounded border-[var(--border-primary)] bg-[var(--bg-primary)] text-[var(--accent-blue)] focus:ring-2 focus:ring-[var(--accent-blue)]/50 focus:ring-offset-0 cursor-pointer"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
                            My service requires IP whitelisting
                          </span>
                          <p className="text-xs text-[var(--text-muted)] mt-0.5">
                            Check this if your integration service requires you
                            to whitelist specific IP addresses
                          </p>
                        </div>
                      </label>

                      {/* Allowed IPs Display - Only show when checkbox is checked */}
                      {requiresIpWhitelist && (
                        <div className="mt-4 p-4 rounded-lg bg-[var(--surface)] border border-[var(--border-primary)]">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-[var(--text-primary)]">
                                IP Addresses to Whitelist
                              </h3>
                              <p className="text-xs text-[var(--text-secondary)] mt-1">
                                Add these IP addresses to your service&apos;s
                                whitelist to allow our platform to connect
                              </p>
                              {allowedEgressIps.length > 0 ? (
                                <div className="mt-3 space-y-2">
                                  {allowedEgressIps.map((ip, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-2 px-3 py-2 rounded bg-[var(--bg-primary)] border border-[var(--border-secondary)]"
                                    >
                                      <code className="text-sm text-[var(--text-primary)] font-mono">
                                        {ip}
                                      </code>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          navigator.clipboard.writeText(ip);
                                        }}
                                        className="ml-auto inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                                        aria-label={`Copy ${ip}`}
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                        Copy
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="mt-3 text-xs text-[var(--text-muted)] italic">
                                  No allowed IPs configured
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    {fields.map((field) => {
                      const fieldVisualKey = visualFieldKey(field.label, field.key);
                      const fieldInputId = `custom-auth-field-${fieldVisualKey}`;
                      const fieldName = `auth.${fieldVisualKey}`;
                      const err = errors[field.key];
                      const showError = Boolean(err) && touched[field.key];

                      const baseInputCls =
                        "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]";
                      const borderCls = showError
                        ? "border-red-500/60 focus:border-red-500"
                        : "border-[var(--border-primary)] focus:border-[var(--border-accent)]";

                      const isSensitiveInput =
                        field.isSecret ||
                        field.inputType === "password" ||
                        field.inputType === "token";

                      const helper = field.description;

                      let placeholder = field.placeholder;

                      const mergedValue =
                        typeof field.value === "string" ? field.value : "";

                      const hasExistingSavedValue =
                        isSensitiveInput && mergedValue.trim() !== "";

                      if (hasExistingSavedValue) placeholder = mergedValue;

                      if (field.inputType === "select") {
                        const opts = (field as any).options ?? [];
                        return (
                          <div key={field.key}>
                            <label
                              htmlFor={fieldInputId}
                              className="block text-sm text-[var(--text-secondary)]"
                            >
                              {field.label}
                              {field.required ? (
                                <span className="text-[var(--text-muted)]"> *</span>
                              ) : null}
                            </label>

                            {helper ? (
                              <div className="text-xs text-[var(--text-muted)] mt-1">
                                {helper}
                              </div>
                            ) : null}

                            <select
                              id={fieldInputId}
                              name={fieldName}
                              data-auth-field={fieldVisualKey}
                              data-auth-field-key={field.key}
                              data-auth-field-label={field.label}
                              className={`${baseInputCls} ${borderCls} mt-2`}
                              value={values[field.key] ?? ""}
                              onChange={(e) =>
                                setFieldValue(field.key, e.target.value)
                              }
                              onBlur={() =>
                                setTouched((p) => ({ ...p, [field.key]: true }))
                              }
                            >
                              <option value="" disabled>
                                {field.placeholder || "Select..."}
                              </option>
                              {Array.isArray(opts)
                                ? opts.map((opt: any) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))
                                : null}
                            </select>

                            {showError ? (
                              <div className="text-xs text-red-400 mt-1">
                                {err}
                              </div>
                            ) : null}
                          </div>
                        );
                      }

                      return (
                        <div key={field.key}>
                          <label
                            htmlFor={fieldInputId}
                            className="block text-sm text-[var(--text-secondary)]"
                          >
                            {field.label}
                            {field.required ? (
                              <span className="text-[var(--text-muted)]"> *</span>
                            ) : null}
                          </label>

                          {helper ? (
                            <div className="text-xs text-[var(--text-muted)] mt-1">
                              {helper}
                            </div>
                          ) : null}

                          <div className="relative mt-2">
                            <input
                              id={fieldInputId}
                              name={fieldName}
                              data-auth-field={fieldVisualKey}
                              data-auth-field-key={field.key}
                              data-auth-field-label={field.label}
                              type={
                                isSensitiveInput && !revealed[field.key]
                                  ? "password"
                                  : "text"
                              }
                              className={`${baseInputCls} ${borderCls} pr-10`}
                              value={values[field.key] ?? ""}
                              placeholder={placeholder}
                              onChange={(e) =>
                                setFieldValue(field.key, e.target.value)
                              }
                              onBlur={() =>
                                setTouched((p) => ({
                                  ...p,
                                  [field.key]: true,
                                }))
                              }
                              autoComplete="off"
                              spellCheck={false}
                            />

                            {isSensitiveInput &&
                            (values[field.key] ?? "").length > 0 ? (
                              <button
                                type="button"
                                onClick={() => revealFieldFor1s(field.key)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                aria-label="Reveal value"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            ) : null}
                          </div>

                          {isSensitiveInput && hasExistingSavedValue ? (
                            <div className="text-xs text-[var(--text-muted)] mt-1">
                              Leave empty to keep the existing value, or enter a
                              new value to replace.
                            </div>
                          ) : null}

                          {showError ? (
                            <div className="text-xs text-red-400 mt-1">
                              {err}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                    </>
                  ) : null}

                  <div
                    className={[
                      shouldShowConnectionFields ? "mt-6" : "",
                      "rounded-lg border border-[var(--border-primary)] bg-[var(--surface)] p-4",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                        Access
                      </h3>
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        Control which tools and entity groups this connection
                        exposes to agents.
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-primary)] px-3 py-2">
                        <span>
                          <span className="block text-sm font-medium text-[var(--text-primary)]">
                            Read
                          </span>
                          <span className="block text-xs text-[var(--text-muted)]">
                            List and inspect data
                          </span>
                        </span>
                        <input
                          id="custom-auth-access-read"
                          name="access.read"
                          data-auth-field="access-read"
                          data-auth-field-key="access.tools.read"
                          data-auth-field-label="Read"
                          type="checkbox"
                          checked
                          disabled
                          className="h-4 w-4 rounded border-[var(--border-primary)] text-[var(--accent-blue)] disabled:opacity-60"
                        />
                      </label>

                      <label
                        htmlFor="custom-auth-access-write"
                        className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-primary)] px-3 py-2 cursor-pointer"
                      >
                        <span>
                          <span className="block text-sm font-medium text-[var(--text-primary)]">
                            Write
                          </span>
                          <span className="block text-xs text-[var(--text-muted)]">
                            Create or update records
                          </span>
                        </span>
                        <input
                          id="custom-auth-access-write"
                          name="access.write"
                          data-auth-field="access-write"
                          data-auth-field-key="access.tools.write"
                          data-auth-field-label="Write"
                          type="checkbox"
                          checked={access.tools.write}
                          onChange={(event) =>
                            setAccess((prev) => ({
                              ...prev,
                              tools: {
                                ...prev.tools,
                                read: true,
                                write: event.target.checked,
                              },
                            }))
                          }
                          className="h-4 w-4 rounded border-[var(--border-primary)] text-[var(--accent-blue)]"
                        />
                      </label>
                    </div>

                    <div className="mt-4">
                      <div className="text-sm font-medium text-[var(--text-primary)]">
                        Entities
                      </div>
                      <div className="mt-2 inline-flex rounded-lg border border-[var(--border-primary)] bg-[var(--bg-primary)] p-1">
                        {[
                          { value: "all", label: "All entities" },
                          { value: "allow", label: "Selected entities" },
                        ].map((option) => {
                          const selected = access.entities.mode === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setAccess((prev) => ({
                                  ...prev,
                                  entities: {
                                    ...prev.entities,
                                    mode: option.value as "all" | "allow",
                                  },
                                }))
                              }
                              className={[
                                "px-3 py-1.5 rounded-md text-sm transition-colors",
                                selected
                                  ? "bg-[var(--bg-tertiary)] text-[var(--text-primary)]"
                                  : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                              ].join(" ")}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>

                      {access.entities.mode === "allow" ? (
                        <div className="mt-3">
                          <label
                            htmlFor="custom-auth-entity-allow"
                            className="block text-xs text-[var(--text-muted)]"
                          >
                            Entity names
                          </label>
                          <textarea
                            id="custom-auth-entity-allow"
                            name="access.entities.allow"
                            data-auth-field="entity-names"
                            data-auth-field-key="access.entities.allow"
                            data-auth-field-label="Entity names"
                            value={entityAllowText}
                            onChange={(event) =>
                              setEntityAllowText(event.target.value)
                            }
                            placeholder="Customers, General Ledger, Bank Transactions"
                            rows={3}
                            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none focus:border-[var(--border-accent)]"
                          />
                          <p className="mt-1 text-xs text-[var(--text-muted)]">
                            Use commas or new lines. Empty selected access means
                            no entity tools are exposed.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 rounded-lg border border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="px-4 py-2 rounded-lg bg-[var(--accent-blue)] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                    >
                      {submitting
                        ? type === "oauth"
                          ? "Opening..."
                          : "Saving..."
                        : type === "oauth"
                          ? "Continue"
                          : "Save"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {status ? (
              <StatusModal
                isOpen={true}
                type={status.type}
                message={status.message}
                onClose={() => setStatus(null)}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};
